import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const CART_SCOPE_KEY = "cartScope";
export const READONLY_PERMS = ["read", "view"];
export const canEditCart = (cart) => !!cart && !READONLY_PERMS.includes(cart.my_permission);

export const CartProvider = ({ children }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isMobile = pathname.startsWith("/m");
  const [carts, setCarts] = useState([]);
  const [activeCartId, setActiveCartId] = useState(localStorage.getItem("activeCartId") || null);
  const [loaded, setLoaded] = useState(false);
  // iter 282: desktop sales-rep filter ("mine" | "all" | "unassigned" | user_id), remembered per user; mobile always uses the default list.
  const scopeKey = user ? `${CART_SCOPE_KEY}:${user.id}` : CART_SCOPE_KEY;
  const [cartScope, setCartScopeState] = useState(() => localStorage.getItem(scopeKey) || "mine");
  useEffect(() => { setCartScopeState(localStorage.getItem(scopeKey) || "mine"); }, [scopeKey]);
  const setCartScope = (scope) => { localStorage.setItem(scopeKey, scope); setCartScopeState(scope); };
  const effectiveScope = isMobile ? null : cartScope;

  const refreshCarts = useCallback(async () => {
    try {
      const { data } = await api.get("/carts", { params: effectiveScope ? { scope: effectiveScope } : {} });
      setCarts(data);
      setLoaded(true);
      return data;
    } catch {
      return [];
    }
  }, [effectiveScope]);

  useEffect(() => {
    // Re-fetch whenever a user becomes authenticated or the scope changes.
    if (user) refreshCarts();
  }, [user, refreshCarts]);

  // Keep the active cart selectable (desktop) even when it falls outside the current scope filter; mobile just falls back.
  useEffect(() => {
    if (!loaded) return;
    if (!activeCartId) { if (carts.length > 0) setActiveCartId(carts[0].id); return; }
    if (carts.some((c) => c.id === activeCartId)) return;
    if (isMobile) { if (carts.length > 0) setActiveCartId(carts[0].id); return; }
    let cancelled = false;
    api.get(`/carts/${activeCartId}/summary`)
      .then(({ data }) => { if (!cancelled) setCarts((cs) => (cs.some((c) => c.id === data.id) ? cs : [...cs, { ...data, outside_scope: true }])); })
      .catch(() => { if (!cancelled && carts.length > 0) setActiveCartId(carts[0].id); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, carts, activeCartId]);

  useEffect(() => {
    if (activeCartId) localStorage.setItem("activeCartId", activeCartId);
  }, [activeCartId]);

  const createCart = async (name, extra = {}) => {
    const { data } = await api.post("/carts", { name, ...extra });
    if (!isMobile && cartScope !== "mine" && cartScope !== "all") setCartScope("mine");
    else await refreshCarts();
    setActiveCartId(data.id);
    return data;
  };

  const deleteCart = async (cartId) => {
    await api.delete(`/carts/${cartId}`);
    await refreshCarts();
  };

  const activeCart = carts.find((c) => c.id === activeCartId) || null;

  const addToCart = async (productId, entryIds, cartonQtys, sampleQtys) => {
    if (!activeCartId) {
      toast.error("Please create a quotation first");
      return;
    }
    if (activeCart && !canEditCart(activeCart)) {
      toast.error(t("cart.readonly_add_hint"));
      return;
    }
    try {
      const body = { product_id: productId };
      if (entryIds?.length) body.entry_ids = entryIds;
      if (cartonQtys && Object.keys(cartonQtys).length) body.carton_qtys = cartonQtys;
      if (sampleQtys && Object.keys(sampleQtys).length) body.sample_qtys = sampleQtys;
      await api.post(`/carts/${activeCartId}/add-product`, body);
      toast.success(`Added to quotation "${activeCart?.name || ""}"`);
      refreshCarts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add to quotation");
    }
  };

  return (
    <CartContext.Provider value={{ carts, activeCartId, setActiveCartId, activeCart, loaded, refreshCarts, createCart, deleteCart, addToCart, cartScope, setCartScope, isMobile }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
